import { Botao, Campo } from "@plantus/ui";
import type { OrdemDaTabela } from "@plantus/ui";

import {
  FILTRO_VAZIO, ORDEM_PADRAO, SITUACOES_DO_FILTRO, contarFiltros, haOQueLimpar,
} from "./ordemDosLotes";
import type { FiltroDeLotes } from "./ordemDosLotes";

/**
 * ONDE ESTÁ X, e O QUE FAÇO AGORA — as duas perguntas na mesma barra, em
 * pontas opostas.
 *
 * <h2>🔴 O pedido, literal</h2>
 *
 * <blockquote>"Hoje 'Receber material' está colado no campo de busca; são
 * gestos opostos."</blockquote>
 *
 * <p>Estavam os três no mesmo `flex`, com o mesmo `gap`: <i>buscar</i>,
 * <i>filtrar</i> e <b>receber</b> apareciam como três variações do mesmo gesto.
 * Dois deles não mudam nada — refazem uma consulta. O terceiro abre o
 * formulário que <b>cria um lote</b>, com código interno e etiqueta de
 * quarentena, e é o único irreversível dos três (nada é apagado neste sistema).
 *
 * <p>A barra passou a ter duas metades declaradas: <code>__achar</code>, que
 * cresce, e <code>__criar</code>, empurrado para a ponta oposta, com um filete
 * entre os dois na mesa. A ordem também é a das perguntas: achar é a 3ª,
 * receber é a 4ª — a última coisa da barra, lida por último.
 *
 * <h2>⚠️ E os dois ficaram DENTRO de `.lotes__acoes`</h2>
 *
 * <p>Não por inércia: a auditoria de papel (`impressao.teste.tsx`) julga o
 * botão primário pelo seletor <code>.lotes__acoes .ui-botao--primaria</code> e
 * o campo por <code>.lotes__acoes .ui-campo</code>. Levar o "Receber material"
 * para o cabeçalho — que era a outra composição possível — apagaria aqueles
 * dois seletores, e o controle negativo daquela auditoria passaria a olhar
 * para o nada: <b>um teste de papel que aprova sem ter visto</b>. A separação
 * que o PO pediu é de LEITURA, e ela se resolve dentro da mesma barra.
 */
interface PropsDaBarra {
  filtro: FiltroDeLotes;
  definirFiltro: (f: FiltroDeLotes) => void;
  filtrando: boolean;
  definirFiltrando: (aberto: boolean) => void;
  /** Receber exige UMA empresa: no consolidado não existe lote de todas. */
  podeReceber: boolean;
  aoReceber: () => void;
  /** A posição na grade do painel (ADR-0013 §4). Repassada ao bloco. */
  "data-bloco": "coluna" | "fileira";
}

export function BarraDosLotes(props: PropsDaBarra) {
  const { filtro, definirFiltro, filtrando, definirFiltrando, podeReceber, aoReceber } = props;
  const quantosFiltros = contarFiltros(filtro);

  return (
    <div className="lotes__acoes" data-bloco={props["data-bloco"]}>
      <div className="lotes__achar">
        <Campo
          rotulo="Buscar"
          value={filtro.termo}
          onChange={(e) => definirFiltro({ ...filtro, termo: e.target.value })}
          /* A dica lista o que a busca ALCANÇA, e ela cresceu junto com o
             servidor: quem cola 32 hexadecimais do adesivo aqui precisa saber
             que isso é um caminho — e quem procura por um PEDAÇO do token
             precisa saber que não é (o token compara por igualdade, e trecho
             de identificador opaco seria varredura por tentativa).
             ⚠️ CURTA POR MEDIÇÃO. A versão de duas linhas fazia a barra subir
             de 102px para 123px — 21px antes do primeiro lote, todo dia, para
             uma frase que se lê uma vez na vida. */
          dica="Lote, matéria-prima, nota fiscal, token ou plaqueta."
        />

        {/* ── O GATILHO DOS FILTROS ────────────────────────────────────
            ⚠️ ELES NASCERAM ABERTOS E FORAM MEDIDOS: a faixa custava 252px
            antes do primeiro lote em 1280 — mais do que os 101px que a
            densidade tinha acabado de devolver. Fechada ela custa ZERO,
            porque mora na barra que já existe. E a contagem no rótulo é o
            que impede o caso perigoso: uma lista recortada que parece a
            lista inteira. */}
        <Botao
          aparencia="secundaria"
          aria-expanded={filtrando}
          onClick={() => definirFiltrando(!filtrando)}
        >
          {quantosFiltros === 0 ? "Filtros" : `Filtros · ${quantosFiltros}`}
        </Botao>
      </div>

      {/* CRIAR. Do outro lado do filete, e é a única coisa nesta tela que
          escreve no banco. Desabilitado no consolidado, e não escondido: sumir
          faria a pessoa procurá-lo; clicável abriria um formulário que não tem
          para qual CNPJ gravar. */}
      <div className="lotes__criar">
        <Botao onClick={aoReceber} disabled={!podeReceber}>
          Receber material
        </Botao>
      </div>
    </div>
  );
}

interface PropsDaFaixa {
  filtro: FiltroDeLotes;
  definirFiltro: (f: FiltroDeLotes) => void;
  ordem: OrdemDaTabela | null;
  definirOrdem: (o: OrdemDaTabela | null) => void;
  /** O período está invertido? A recusa fica ao lado do campo que a causou. */
  invertido: boolean;
  "data-bloco": "coluna" | "fileira";
}

/**
 * A faixa de filtros — fina, sem cartão em volta: eles RECORTAM a lista, não
 * são um assunto à parte dela. Recortar é do SERVIDOR (feature 1.3.1 R15).
 */
export function FaixaDeFiltros(props: PropsDaFaixa) {
  const { filtro, definirFiltro, ordem, definirOrdem, invertido } = props;

  return (
    <div className="lotes__filtros" data-bloco={props["data-bloco"]}>
      {/* ⚠️ NÃO é `SeletorMultiplo`, e a razão é medida: aquele componente é
          uma GRADE (`repeat(auto-fill, minmax(4rem, 1fr))`), desenhada para os
          vinte números de volume da tela de etiquetas. Numa faixa de filtro ele
          recebe a largura do conteúdo, o `auto-fill` fecha em UMA coluna e as
          cinco situações empilham: 252px medidos em Chrome, para cinco
          palavras. Aqui são cinco botões da casa com `aria-pressed` — a MESMA
          gramática visual, composta em vez de recriada. */}
      <div className="lotes__situacoes" role="group" aria-label="Situação">
        {SITUACOES_DO_FILTRO.map((situacao) => {
          const escolhida = filtro.situacoes.includes(situacao.id);
          return (
            <Botao
              key={situacao.id}
              aparencia={escolhida ? "primaria" : "secundaria"}
              aria-pressed={escolhida}
              onClick={() =>
                definirFiltro({
                  ...filtro,
                  // Nenhuma escolhida é TODAS, e não nenhuma — é o que o
                  // servidor faz com a lista vazia. Desmarcar a última devolve
                  // a lista inteira, em vez de esvaziar a tela.
                  situacoes: escolhida
                    ? filtro.situacoes.filter((s) => s !== situacao.id)
                    : [...filtro.situacoes, situacao.id],
                })
              }
            >
              {situacao.rotulo}
            </Botao>
          );
        })}
      </div>

      <div className="lotes__periodo">
        {/* O período é o de RECEBIMENTO, com os dois extremos inclusivos.
            "O que vence primeiro" não é um segundo intervalo: é a ordenação
            por Validade, no cabeçalho da tabela. */}
        <Campo
          rotulo="Recebido de"
          type="date"
          value={filtro.de}
          onChange={(e) => definirFiltro({ ...filtro, de: e.target.value })}
          {...(invertido ? { erro: "O início vem depois do fim." } : {})}
        />
        <Campo
          rotulo="até"
          type="date"
          value={filtro.ate}
          onChange={(e) => definirFiltro({ ...filtro, ate: e.target.value })}
        />
      </div>

      {/* Só aparece quando há o que limpar. Um "Limpar" permanentemente aceso
          numa tela sem filtro é um controle que não faz nada — e é também como
          se volta à ordem padrão (`recebidoEm desc`), que nenhuma coluna
          oferece num clique só. */}
      {haOQueLimpar(filtro, ordem) && (
        <Botao
          aparencia="texto"
          onClick={() => {
            definirFiltro(FILTRO_VAZIO);
            definirOrdem(ORDEM_PADRAO);
          }}
        >
          Limpar filtros
        </Botao>
      )}
    </div>
  );
}
